// Pure quote math. Components render; every derivation of a line total or a
// rollup lives here so it can be unit-tested without React or a store.
// Sums accumulate cents-level float drift, so every displayed figure is rounded
// to two decimals once, at the boundary.
const roundCents = (amount) => Math.round(amount * 100) / 100;
// Builds an addable draft from a rate-plan view plus the property it belongs to.
// The rate plan already carries the raw per-unit breakdown; this only names it.
export const buildQuoteLineDraft = (ratePlan, roomTypeName, property) => ({
    propertyId: property.id,
    propertyName: property.name,
    roomTypeName,
    ratePlanName: ratePlan.ratePlanName,
    arrival: ratePlan.arrival,
    departure: ratePlan.departure,
    nights: ratePlan.nights,
    currency: ratePlan.currency,
    roomChargesAmount: ratePlan.roomChargesAmount,
    taxesAndFeesAmount: ratePlan.taxesAndFeesAmount,
    totalAmount: ratePlan.totalAmount,
    availableUnits: ratePlan.availableUnits
});
// Two lines describe the same offer when they name the same stay: property,
// room type, rate plan, and exact dates. Adding an offer already in the quote
// increments that line instead of creating a duplicate.
export const isSameOffer = (a, b) => a.propertyId === b.propertyId &&
    a.roomTypeName === b.roomTypeName &&
    a.ratePlanName === b.ratePlanName &&
    a.arrival === b.arrival &&
    a.departure === b.departure;
// Two lines draw on the same room-type inventory when they name the same stay —
// property, room type, and exact dates — regardless of rate plan. Availability
// is tracked per room type (unit group), not per rate plan, so every rate plan
// of a room type shares one pool: staging one Studio on the flexible rate leaves
// nothing to add on the prepaid rate.
export const isSameRoomStay = (a, b) => a.propertyId === b.propertyId &&
    a.roomTypeName === b.roomTypeName &&
    a.arrival === b.arrival &&
    a.departure === b.departure;
// How many rooms of a room type are already staged for a stay — the summed
// quantity of every line drawing on the same room-type inventory, across all
// rate plans.
export const stagedRoomUnits = (items, offer) => items
    .filter(item => isSameRoomStay(item, offer))
    .reduce((sum, item) => sum + item.quantity, 0);
// One line's total: quantity times the per-unit offer total.
export const lineItemTotal = (item) => roundCents(item.quantity * item.totalAmount);
// Rolls the whole quote up. Room charges, taxes & fees, and total each fan
// across the items as quantity times the per-unit amount. The currency is taken
// from the first line — a v1 quote is assumed single-currency.
export const computeRollup = (items) => {
    const sumBy = (pick) => roundCents(items.reduce((sum, item) => sum + item.quantity * pick(item), 0));
    return {
        roomCharges: sumBy(item => item.roomChargesAmount),
        taxesAndFees: sumBy(item => item.taxesAndFeesAmount),
        total: sumBy(item => item.totalAmount),
        currency: items[0]?.currency ?? 'USD'
    };
};
